import { Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import type { PmePolicyView } from './map-policy-to-pme-view.util';
import { PmePolicyCoveragesServices } from './pme-policy-coverages-services.component';
import { PmePolicyPremiumCard } from './pme-policy-premium-card.component';
import { PmeAccordionSection, PmeInfoRow } from './pme-policy-section';
import { PmePolicyStipulantCard } from './pme-policy-stipulant-card.component';

type Props = {
  policyId: string;
  view: PmePolicyView;
};

/** Conteúdo da aba "Apólice" da apólice PME — o layout do mockup. */
export function PmePolicyTab({ policyId, view }: Props) {
  const [openStipulants, setOpenStipulants] = useState<Set<string>>(new Set());

  const { premium, emission, stipulantHeader, entities, commercialization } =
    view;

  const allOpen =
    entities.length > 0 &&
    entities.every(entity => openStipulants.has(entity.targetId));

  function toggleStipulant(targetId: string) {
    setOpenStipulants(prev => {
      const next = new Set(prev);
      if (next.has(targetId)) next.delete(targetId);
      else next.add(targetId);
      return next;
    });
  }

  function toggleAll() {
    setOpenStipulants(
      allOpen ? new Set() : new Set(entities.map(entity => entity.targetId))
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <div className='mb-4'>
        <PmePolicyPremiumCard data={premium} />
      </div>

      <PmeAccordionSection title='Dados da Emissão'>
        <div className='grid grid-cols-2 gap-6 sm:grid-cols-4'>
          <PmeInfoRow
            label='Início de Vigência'
            value={emission.effectiveDate}
          />
          <PmeInfoRow label='Fim de Vigência' value={emission.expiryDate} />
          <PmeInfoRow label='Data da Emissão' value={emission.issueDate} />
          <PmeInfoRow label='Tipo de Seguro' value={emission.insuranceType} />
        </div>
      </PmeAccordionSection>

      <PmeAccordionSection title='Dados do Estipulante'>
        <div className='grid grid-cols-2 gap-6 sm:grid-cols-4'>
          <PmeInfoRow label='CNPJ' value={stipulantHeader.document} />
          <PmeInfoRow label='Razão Social' value={stipulantHeader.socialName} />
          <PmeInfoRow label='E-mail' value={stipulantHeader.email} />
          <PmeInfoRow label='Telefone' value={stipulantHeader.phone} />
        </div>
      </PmeAccordionSection>

      <PmeAccordionSection title='Dados do Risco'>
        <div className='flex flex-col items-start gap-4 self-stretch'>
          <div className='flex w-full items-center justify-between'>
            <Text className='text-base text-alba-black-500'>
              Lista de Estipulantes
            </Text>
            {entities.length > 0 && (
              <button
                type='button'
                onClick={toggleAll}
                className='flex cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent px-4 py-2 text-alba-blue-500 transition-colors hover:bg-alba-gray-100'
              >
                <span className='text-xs font-medium'>
                  {allOpen
                    ? `Fechar Todos (${entities.length})`
                    : `Abrir Todos (${entities.length})`}
                </span>
                {allOpen ? (
                  <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
                ) : (
                  <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
                )}
              </button>
            )}
          </div>

          <div className='flex w-full flex-col gap-2'>
            {entities.length === 0 ? (
              <Text className='text-sm text-alba-gray-600'>
                Nenhum estipulante encontrado para esta apólice.
              </Text>
            ) : (
              entities.map(entity => (
                <PmePolicyStipulantCard
                  key={entity.targetId}
                  policyId={policyId}
                  entity={entity}
                  isOpen={openStipulants.has(entity.targetId)}
                  onToggle={() => toggleStipulant(entity.targetId)}
                />
              ))
            )}
          </div>
        </div>
      </PmeAccordionSection>

      <PmeAccordionSection title='Coberturas e Serviços'>
        <PmePolicyCoveragesServices data={view.coveragesServices} />
      </PmeAccordionSection>

      <PmeAccordionSection title='Comercialização'>
        <div className='flex flex-col gap-4'>
          <Text className='text-sm font-bold text-alba-black-500'>
            Dados do Corretor
          </Text>
          <div className='grid grid-cols-2 gap-6 sm:grid-cols-4'>
            <PmeInfoRow label='Corretor' value={commercialization.broker} />
            <PmeInfoRow
              label='Percentual de Corretagem'
              value={commercialization.brokeragePercent}
            />
            <PmeInfoRow
              label='Percentual de Agenciamento'
              value={commercialization.agencingPercent}
            />
            <PmeInfoRow
              label='Agravo'
              value={commercialization.surchargePercent}
            />
            {commercialization.leaderSharePercent && (
              <PmeInfoRow
                label='Participação'
                value={commercialization.leaderSharePercent}
              />
            )}
          </div>

          {commercialization.coBrokers.length > 0 && (
            <>
              <Text className='pt-2 text-sm font-bold text-alba-black-500'>
                Co-corretores
              </Text>
              <div className='flex flex-col gap-3'>
                {commercialization.coBrokers.map((coBroker, index) => (
                  <div
                    key={`${coBroker.susepCode}-${index}`}
                    className='grid grid-cols-2 gap-6 rounded-xl border border-alba-gray-200 px-4 py-3 sm:grid-cols-4'
                  >
                    <PmeInfoRow label='Co-corretor' value={coBroker.name} />
                    <PmeInfoRow label='SUSEP' value={coBroker.susepCode} />
                    <PmeInfoRow label='CNPJ' value={coBroker.document} />
                    <PmeInfoRow
                      label='Participação'
                      value={coBroker.sharePercent}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </PmeAccordionSection>
    </div>
  );
}
